/**
 * Product Card Component
 * Main card component for displaying product features
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Switch, Dropdown, Skeleton, message, Modal, Typography } from 'antd';
import { 
  SettingOutlined, 
  BarChartOutlined, 
  FileTextOutlined,
  ExclamationCircleOutlined,
  MoreOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { ProductFeature } from '../../services/productFeaturesService';
import { UserRole } from '../../types';
import ProductMetrics from './ProductMetrics';
import ProductEmptyState from './ProductEmptyState';
import './ProductCard.css';

const { Title } = Typography;

interface ProductCardProps {
  product: ProductFeature;
  onEnable: (code: string) => Promise<void>;
  onDisable: (code: string) => Promise<void>;
  userRole: UserRole;
  loading?: boolean;
}

const PRODUCT_COLORS: Record<string, string> = {
  fashsnap: '#1890ff',
  fashplay: '#52c41a',
  fashmatch: '#fa8c16',
};

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onEnable,
  onDisable,
  userRole,
  loading = false,
}) => {
  const navigate = useNavigate();
  const [isDisabling, setIsDisabling] = useState(false);

  const canManage = userRole === 'admin' || userRole === 'manager';
  const productColor = PRODUCT_COLORS[product.code] || '#1890ff';
  const hasVideoSupport = ['fashsnap', 'fashplay'].includes(product.code);

  const handleToggle = async (checked: boolean) => {
    if (checked) {
      try {
        await onEnable(product.code);
        message.success(`${product.name} aktifleştirildi`);
      } catch (error) {
        message.error('İşlem başarısız, lütfen tekrar deneyin');
      }
    } else {
      Modal.confirm({
        title: 'Ürünü Devre Dışı Bırak',
        icon: <ExclamationCircleOutlined />,
        content: `${product.name} ürününü devre dışı bırakmak istediğinize emin misiniz?`,
        okText: 'Evet',
        cancelText: 'Hayır',
        onOk: async () => {
          setIsDisabling(true);
          try {
            await onDisable(product.code);
            message.success(`${product.name} devre dışı bırakıldı`);
          } catch (error) {
            message.error('İşlem başarısız, lütfen tekrar deneyin');
          } finally {
            setIsDisabling(false);
          }
        },
      });
    }
  };

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    switch (key) {
      case 'videos':
        navigate(`/video-management/${product.code}`);
        break;
      case 'settings':
        message.info('Ayarlar sayfası yakında eklenecek');
        break;
      case 'analytics':
        message.info('Detaylı analitik sayfası yakında eklenecek');
        break;
      case 'docs':
        window.open(`https://www.fashcortex.com/docs/${product.code}`, '_blank');
        break;
    }
  };

  const menuItems: MenuProps['items'] = [
    ...(hasVideoSupport
      ? [
          {
            key: 'videos',
            label: 'Videoları Yönet',
            icon: <VideoCameraOutlined />,
            disabled: !product.enabled,
          },
        ]
      : []),
    {
      key: 'settings',
      label: 'Ayarlar',
      icon: <SettingOutlined />,
      disabled: !product.enabled,
    },
    {
      key: 'analytics',
      label: 'Detaylı Analitik',
      icon: <BarChartOutlined />,
      disabled: !product.enabled,
    },
    {
      type: 'divider',
    },
    {
      key: 'docs',
      label: 'API Dokümantasyonu',
      icon: <FileTextOutlined />,
    },
  ];

  return (
    <Card
      className={`product-card ${product.enabled ? 'enabled' : 'disabled'}`}
      style={{ 
        borderColor: product.enabled ? productColor : '#d9d9d9',
        borderWidth: 2,
      }}
    >
      {/* Card Header */}
      <div className="product-card-header">
        <div className="product-header-left">
          <div 
            className="product-logo"
            style={{ 
              backgroundColor: `${productColor}10`,
              borderColor: productColor,
            }}
          >
            <img
              src={`/assets/products/${product.code}-logo.png`}
              alt={product.name}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </div>
          <div className="product-header-info">
            <Title level={4} style={{ margin: 0, color: '#262626' }}>
              {product.name}
            </Title>
            {product.enabled && (
              <span className="status-badge enabled">Aktif</span>
            )}
          </div>
        </div>
        <div className="product-header-actions">
          {canManage && (
            <Switch
              checked={product.enabled}
              onChange={handleToggle}
              loading={isDisabling}
              style={{
                backgroundColor: product.enabled ? productColor : undefined,
              }}
            />
          )}
          <Dropdown
            menu={{ items: menuItems, onClick: handleMenuClick }}
            trigger={['click']}
            placement="bottomRight"
          >
            <MoreOutlined 
              style={{ 
                fontSize: 20, 
                cursor: 'pointer',
                color: '#8c8c8c',
                padding: '4px 8px',
              }}
            />
          </Dropdown>
        </div>
      </div>

      {/* Card Content */}
      <div className="product-card-content">
        {loading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : product.enabled && product.metrics ? (
          <ProductMetrics metrics={product.metrics} productColor={productColor} />
        ) : (
          <ProductEmptyState
            productCode={product.code}
            productName={product.name}
            description={product.description}
            onEnable={() => onEnable(product.code)}
            canEnable={canManage}
            isEnabling={false}
          />
        )}
      </div>
    </Card>
  );
};

export default ProductCard;
